"use client";

import { useState } from "react";

import Image from "next/image";
import { useTranslations } from "next-intl";

import elena from "@/app/assets/parents/elena.sm.webp";
import jen from "@/app/assets/parents/jen.sm.webp";
import maria from "@/app/assets/parents/maria.sm.webp";

import { CloudBreak } from "./cloudBreak";

const testimonials = [
    {
        image: jen,
        key: "jen",
        link: "https://www.trustpilot.com/reviews/67a1eec3f3fe31524e131e46",
        rating: 5,
    },
    {
        image: elena,
        key: "elena",
        link: "https://www.trustpilot.com/reviews/679bf346e42d52e9378f9f7e",
        rating: 5,
    },
    {
        image: maria,
        key: "maria",
        link: "https://www.trustpilot.com/reviews/67acf56e2005619a0ac8a784",
        rating: 5,
    },
];

const StarRating = ({ rating }: { rating: number }) => {
    return (
        <div className="flex">
            {[...Array(5)].map((_, index) => (
                <div
                    className={`w-5 h-5 sm:w-6 sm:h-6 mr-1 flex items-center justify-center ${
                        index < rating ? "bg-tpGreen" : "bg-gray-300"
                    }`}
                    key={index}
                >
                    <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                </div>
            ))}
        </div>
    );
};

export const TestimonialsSection = () => {
    const t = useTranslations("Testimonials");
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

    const toggleExpand = (key: string) => {
        setExpandedItems((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    return (
        <section className="bg-lights-100 ">
            <div className="container mx-auto px-4 sm:px-4">
                <h2 className="text-3xl md:text-4xl text-center font-medium mb-10 md:mb-14" id="testimonials">
                    {t("title")}
                </h2>

                <div className="max-w-3xl mx-auto space-y-8 md:space-y-10">
                    {testimonials.map((testimonial) => {
                        const isExpanded = expandedItems[testimonial.key] || false;

                        return (
                            <div className="bg-white rounded-[24px] p-6 sm:p-8" key={testimonial.key}>
                                {/* Quote Content */}
                                <div className="relative mb-7">
                                    {/* Top Quote Mark */}
                                    <div
                                        className="absolute -top-3 -left-1 text-primary-100/20"
                                        style={{ fontSize: "3rem" }}
                                    >
                                        &ldquo;
                                    </div>

                                    {/* Testimonial Text */}
                                    <div className="relative px-6 pt-4">
                                        {/* Testimonial Title */}
                                        <h3 className="text-lg sm:text-xl font-semibold text-primary-100 mb-2">
                                            {t(`items.${testimonial.key}.title`)}
                                        </h3>

                                        {/* Testimonial Content */}
                                        <div
                                            className={`text-base sm:text-lg overflow-hidden transition-all duration-300 relative ${
                                                isExpanded || t(`items.${testimonial.key}.text`).length <= 300
                                                    ? ""
                                                    : "max-h-24 sm:max-h-28"
                                            }`}
                                        >
                                            {t(`items.${testimonial.key}.text`)}
                                            {!isExpanded && t(`items.${testimonial.key}.text`).length > 300 && (
                                                <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-white to-transparent" />
                                            )}
                                        </div>

                                        {t(`items.${testimonial.key}.text`).length > 300 && (
                                            <button
                                                className="text-primary-100 font-medium hover:underline focus:outline-none text-sm mt-2"
                                                onClick={() => toggleExpand(testimonial.key)}
                                            >
                                                {isExpanded ? "Show less" : "Show more"}
                                            </button>
                                        )}
                                    </div>

                                    {/* Bottom Quote Mark */}
                                    <div
                                        className="absolute -bottom-4 -right-1 text-primary-100/20"
                                        style={{ fontSize: "3rem" }}
                                    >
                                        &rdquo;
                                    </div>
                                </div>

                                {/* Author Section */}
                                <div className="flex items-center border-t border-gray-100 pt-5 mt-2">
                                    {/* Author Image */}
                                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-2 border-primary-100/20 flex-shrink-0">
                                        <Image
                                            alt={t(`items.${testimonial.key}.author`)}
                                            className="object-cover w-full h-full"
                                            height={56}
                                            loading="lazy"
                                            src={testimonial.image}
                                            width={56}
                                        />
                                    </div>

                                    {/* Author Info */}
                                    <div className="ml-4 flex-grow">
                                        <h4 className="text-base sm:text-lg font-medium mb-1">
                                            {t(`items.${testimonial.key}.author`)}
                                        </h4>
                                        <a
                                            className="inline-flex items-center hover:opacity-90 transition-opacity"
                                            href={testimonial.link}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            <StarRating rating={testimonial.rating} />
                                            <div className="flex items-center ml-2">
                                                <svg
                                                    className="w-5 h-5 sm:w-6 sm:h-6 text-tpGreen"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                                <span className="text-base sm:text-lg text-black ml-1">Trustpilot</span>
                                            </div>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
